import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Stack,
  Text,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Spinner,
  TextField,
  Separator,
  IconButton,
  Persona,
  PersonaSize,
  Callout,
  DirectionalHint,
  Icon,
  Toggle,
  TooltipHost,
} from '@fluentui/react';
import { IBestellungKommentar, IBestellpositionKommentar } from '../../shared/models/IKommentar';
import { IBestellposition } from '../../shared/models';
import { IUserPermissions } from '../../shared/models/IUserPermissions';
import { kommentareApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

// Kombinierter Kommentar-Typ für die Anzeige
interface IKommentarAnzeige {
  id: string;
  typ: 'bestellung' | 'position';
  kommentartext: string;
  authorName: string;
  author: string;
  created: Date | null;
  positionId?: number;
  posID?: number;
  artikelname?: string;
  parentKommentarID?: string;
  erwaehntPersonen?: string[];
}

// Thread-Struktur: Haupt-Kommentar mit Antworten
interface IKommentarThread {
  hauptKommentar: IKommentarAnzeige;
  antworten: IKommentarAnzeige[];
}

interface IKommentarSektionProps {
  bestellungId: number;
  positionen: IBestellposition[];
  onKommentarChange?: () => void;
}

interface IUserSuggestion {
  id: string;
  displayName: string;
  mail: string;
}

// Farben für Positions-Badges
const positionColors = [
  { bg: '#e3f2fd', border: '#1976d2', text: '#1565c0' }, // Blau
  { bg: '#e8f5e9', border: '#388e3c', text: '#2e7d32' }, // Grün
  { bg: '#fff3e0', border: '#f57c00', text: '#e65100' }, // Orange
  { bg: '#f3e5f5', border: '#7b1fa2', text: '#6a1b9a' }, // Lila
  { bg: '#e0f7fa', border: '#00838f', text: '#006064' }, // Cyan
  { bg: '#fce4ec', border: '#c2185b', text: '#ad1457' }, // Pink
  { bg: '#fff8e1', border: '#ffa000', text: '#ff6f00' }, // Amber
  { bg: '#e8eaf6', border: '#3f51b5', text: '#303f9f' }, // Indigo
];

const getPositionColor = (posID: number) => {
  return positionColors[(posID - 1) % positionColors.length];
};

export const KommentarSektion: React.FC<IKommentarSektionProps> = ({
  bestellungId,
  positionen,
  onKommentarChange,
}) => {
  const { user } = useAuth();
  const [threads, setThreads] = useState<IKommentarThread[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [neuerKommentar, setNeuerKommentar] = useState('');
  const [saving, setSaving] = useState(false);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [selectedPositionId, setSelectedPositionId] = useState<number | null>(null);
  const [emailToNameMap, setEmailToNameMap] = useState<Map<string, string>>(new Map());

  // Berechtigungen
  const [currentUserPermissions, setCurrentUserPermissions] = useState<IUserPermissions | null>(null);
  const [temporaryPermissions, setTemporaryPermissions] = useState<Map<string, boolean>>(new Map());
  const [mentionedUsersPermissions, setMentionedUsersPermissions] = useState<Map<string, IUserPermissions>>(new Map());

  // Bearbeiten-Zustand: kommentarId → aktueller Editiertext
  const [editingStates, setEditingStates] = useState<Map<string, string>>(new Map());
  const [savingEditId, setSavingEditId] = useState<string | null>(null);

  // @-Mention Zustand
  const [showUserSuggestions, setShowUserSuggestions] = useState(false);
  const [userSuggestions, setUserSuggestions] = useState<IUserSuggestion[]>([]);
  const [mentionSearch, setMentionSearch] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const calloutTargetRef = useRef<HTMLDivElement>(null);
  // Speichere erwähnte Benutzer direkt beim Auswählen (nicht aus Text extrahieren!)
  const [selectedMentions, setSelectedMentions] = useState<Map<string, IUserSuggestion>>(new Map());

  useEffect(() => {
    loadKommentare();
    loadCurrentUserPermissions();
  }, [bestellungId]);

  const loadCurrentUserPermissions = async () => {
    if (!user?.email) return;
    try {
      const response = await axios.get('/api/permissions/me/resource', {
        params: {
          email: user.email,
          bestellungId: bestellungId
        }
      });
      setCurrentUserPermissions(response.data);
    } catch (error) {
      console.error('[Kommentare] Fehler beim Laden der Berechtigungen:', error);
    }
  };

  const loadTemporaryPermissions = async () => {
    try {
      const response = await axios.get('/api/temporary-permissions', {
        params: { bestellungId }
      });
      const tempPermMap = new Map<string, boolean>();
      response.data.forEach((perm: any) => {
        const key = `${perm.UserEmail}_${perm.BestellungId}_${perm.PositionId || 'null'}`;
        tempPermMap.set(key, true);
      });
      setTemporaryPermissions(tempPermMap);
    } catch (error) {
      console.error('[Kommentare] Fehler beim Laden der temporären Berechtigungen:', error);
    }
  };

  const loadMentionedUserPermissions = async (emails: string[]) => {
    const permMap = new Map<string, IUserPermissions>();
    for (const email of emails) {
      try {
        const response = await axios.get('/api/permissions/me/resource', {
          params: {
            email,
            bestellungId: bestellungId
          }
        });
        permMap.set(email, response.data);
      } catch (error) {
        console.error(`[Kommentare] Fehler beim Laden der Berechtigungen für ${email}:`, error);
      }
    }
    setMentionedUsersPermissions(permMap);
  };

  const loadKommentare = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await kommentareApi.getAlleKommentare(bestellungId);

      // Kombiniere und transformiere Kommentare
      // WICHTIG: IDs mit Präfix versehen, da Bestellung- und Position-Kommentare
      // in separaten SharePoint-Listen gespeichert werden und eigene ID-Bereiche haben
      const alleKommentare: IKommentarAnzeige[] = [];

      // Bestellung-Kommentare (Präfix "b_")
      result.bestellungKommentare.forEach((k) => {
        alleKommentare.push({
          id: `b_${k.id}`,
          typ: 'bestellung',
          kommentartext: k.Kommentartext,
          authorName: k.CreatedByUser || k.AuthorName || k.Author || 'Unbekannt',
          author: k.CreatedByUserEmail || k.Author || '',
          created: k.Created ? new Date(k.Created) : null,
          // ParentKommentarID ist auch eine Bestellung-Kommentar-ID
          parentKommentarID: k.ParentKommentarID ? `b_${k.ParentKommentarID}` : undefined,
          erwaehntPersonen: k.ErwaehntPersonen,
        });
      });

      // Position-Kommentare mit Artikelname (Präfix "p_")
      result.positionKommentare.forEach((k) => {
        const position = positionen.find((p) => String(p.Id) === String(k.BestellpositionID));
        // Verwende PosID aus Kommentar, ansonsten aus Position als Fallback
        const posID = k.PosID || position?.PosID;
        alleKommentare.push({
          id: `p_${k.id}`,
          typ: 'position',
          kommentartext: k.Kommentartext,
          authorName: k.CreatedByUser || k.AuthorName || k.Author || 'Unbekannt',
          author: k.CreatedByUserEmail || k.Author || '',
          created: k.Created ? new Date(k.Created) : null,
          positionId: parseInt(k.BestellpositionID, 10),
          posID: posID,
          artikelname: position?.Artikelname || `Position ${posID}`,
          // ParentKommentarID ist auch eine Position-Kommentar-ID
          parentKommentarID: k.ParentKommentarID ? `p_${k.ParentKommentarID}` : undefined,
          erwaehntPersonen: k.ErwaehntPersonen,
        });
      });

      // Thread-Struktur aufbauen
      const hauptKommentare = alleKommentare.filter(
        (k) => !k.parentKommentarID || k.parentKommentarID === ''
      );
      const antworten = alleKommentare.filter(
        (k) => k.parentKommentarID && k.parentKommentarID !== ''
      );

      // Threads erstellen
      const threadList: IKommentarThread[] = hauptKommentare.map((haupt) => ({
        hauptKommentar: haupt,
        antworten: antworten
          .filter((a) => a.parentKommentarID === haupt.id)
          // Antworten aufsteigend sortieren (älteste zuerst)
          .sort((a, b) => {
            const dateA = a.created ? a.created.getTime() : 0;
            const dateB = b.created ? b.created.getTime() : 0;
            return dateA - dateB;
          }),
      }));

      // Threads absteigend nach neuestem Kommentar sortieren (neuester Thread oben)
      threadList.sort((a, b) => {
        // Nimm das neueste Datum im Thread (Hauptkommentar oder letzte Antwort)
        const getNewestDate = (thread: IKommentarThread): number => {
          const hauptDate = thread.hauptKommentar.created?.getTime() || 0;
          const lastAntwortDate =
            thread.antworten.length > 0
              ? thread.antworten[thread.antworten.length - 1].created?.getTime() || 0
              : 0;
          return Math.max(hauptDate, lastAntwortDate);
        };
        return getNewestDate(b) - getNewestDate(a);
      });

      setThreads(threadList);

      // Lade DisplayNames für alle erwähnten Personen (Emails)
      const allEmails = new Set<string>();
      alleKommentare.forEach((k) => {
        if (k.erwaehntPersonen) {
          k.erwaehntPersonen.forEach((email) => allEmails.add(email));
        }
      });

      if (allEmails.size > 0) {
        const emailMap = await convertEmailsToDisplayNames(Array.from(allEmails));
        setEmailToNameMap(emailMap);
        // Lade Berechtigungen für erwähnte Benutzer
        await loadMentionedUserPermissions(Array.from(allEmails));
      }

      // Lade temporäre Berechtigungen
      await loadTemporaryPermissions();
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Kommentare');
    } finally {
      setLoading(false);
    }
  };

  // Benutzersuche für @-Mentions
  const searchUsers = useCallback(async (searchTerm: string) => {
    if (searchTerm.length < 2) {
      setUserSuggestions([]);
      return;
    }

    try {
      const response = await axios.get(`/api/users/search?q=${encodeURIComponent(searchTerm)}`);
      setUserSuggestions(response.data || []);
    } catch (err) {
      console.error('Fehler bei Benutzersuche:', err);
      setUserSuggestions([]);
    }
  }, []);

  // Debounced Benutzersuche
  useEffect(() => {
    if (mentionSearch.length >= 2) {
      const timer = setTimeout(() => searchUsers(mentionSearch), 300);
      return () => clearTimeout(timer);
    } else {
      setUserSuggestions([]);
      return undefined;
    }
  }, [mentionSearch, searchUsers]);

  const handleTextChange = (value: string) => {
    setNeuerKommentar(value);

    // Prüfe auf @-Zeichen für Mention
    const lastAtIndex = value.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const textAfterAt = value.substring(lastAtIndex + 1);
      // Prüfe ob wir noch im Mention-Modus sind (kein Leerzeichen nach @)
      if (!textAfterAt.includes(' ') && textAfterAt.length >= 0) {
        setMentionSearch(textAfterAt);
        setShowUserSuggestions(true);
        setCursorPosition(lastAtIndex);
      } else {
        setShowUserSuggestions(false);
      }
    } else {
      setShowUserSuggestions(false);
    }
  };

  const handleSelectUser = (user: IUserSuggestion) => {
    // Ersetze den @-Text mit dem Benutzernamen
    const beforeAt = neuerKommentar.substring(0, cursorPosition);
    const afterMention = neuerKommentar.substring(cursorPosition + mentionSearch.length + 1);
    const newText = `${beforeAt}@${user.displayName} ${afterMention}`;
    setNeuerKommentar(newText);

    // WICHTIG: Speichere den User direkt in selectedMentions (mit displayName als Key)
    // So müssen wir später nicht aus dem Text extrahieren!
    const newMentions = new Map(selectedMentions);
    newMentions.set(user.displayName, user);
    setSelectedMentions(newMentions);
    console.log(`[Mention] User ausgewählt: ${user.displayName} -> ${user.mail}`);

    setShowUserSuggestions(false);
    setMentionSearch('');
  };

  // Extrahiere erwähnte Personen aus dem Kommentartext
  // Format: @Nachname, Vorname oder @IT-Support oder @Vorname Nachname
  const extractMentions = (text: string): string[] => {
    // Regex für verschiedene Namensformate:
    // - "IT-Support" (mit Bindestrich)
    // - "Nachname, Vorname" (mit Komma + Leerzeichen)
    // - "Vorname Nachname" (mit Leerzeichen)
    // - "Vorname Mittlename Nachname" (mehrere Leerzeichen)
    // Stoppt bei: Punkt, Komma am Ende, Ausrufezeichen, Fragezeichen, Doppelleerzeichen, Zeilenumbruch
    const mentionRegex = /@([\wÄÖÜäöüß\-]+(?:[\s,][\wÄÖÜäöüß\-]+)*)(?=\s*[\.\!\?\n\r]|\s{2,}|$)/g;
    const mentions: string[] = [];
    let match;
    while ((match = mentionRegex.exec(text)) !== null) {
      // Entferne trailing Kommas und Leerzeichen
      const cleanedMention = match[1].trim().replace(/,\s*$/, '');
      if (cleanedMention) {
        mentions.push(cleanedMention);
      }
    }
    return mentions;
  };

  // Konvertiere DisplayNames zu Email-Adressen (inkl. Gruppen-Auflösung)
  const convertMentionsToEmails = async (displayNames: string[]): Promise<string[]> => {
    const emails: string[] = [];

    for (const displayName of displayNames) {
      try {
        console.log(`[Mention] Verarbeite: "${displayName}"`);

        // Suche Benutzer/Gruppe nach DisplayName
        const response = await axios.get(`/api/users/search?q=${encodeURIComponent(displayName)}`);
        console.log(`[Mention] LDAP-Suche für "${displayName}" ergab ${response.data.length} Treffer`);

        // Finde exakte Übereinstimmung (case-insensitive)
        let entity = response.data.find(
          (u: any) => u.displayName.toLowerCase() === displayName.toLowerCase()
        );

        // Falls kein exact match: versuche umgedrehtes Format (Nachname, Vorname <-> Vorname Nachname)
        if (!entity && displayName.includes(',')) {
          // "@Nachname, Vorname" -> suche nach "Vorname Nachname"
          const parts = displayName.split(',').map((p: string) => p.trim());
          if (parts.length === 2) {
            const reversedName = `${parts[1]} ${parts[0]}`;
            console.log(`[Mention] Versuche umgedrehtes Format: "${displayName}" -> "${reversedName}"`);
            entity = response.data.find(
              (u: any) => u.displayName.toLowerCase() === reversedName.toLowerCase()
            );
            if (entity) {
              console.log(`[Mention] ✓ Gefunden via umgedrehtem Format: "${reversedName}" -> ${entity.mail}`);
            }
          }
        }

        // Falls immer noch nicht gefunden: versuche den ersten Treffer (wenn die Suche was zurückgab)
        if (!entity && response.data.length > 0) {
          entity = response.data[0];
          console.log(`[Mention] Verwende ersten Treffer für "${displayName}": ${entity.displayName} (${entity.mail})`);
        }

        if (entity) {
          // Prüfe ob es eine Gruppe ist
          if (entity.isGroup) {
            console.log(`[Mention] "${displayName}" ist eine Gruppe, lade Mitglieder...`);

            try {
              // Lade Gruppenmitglieder
              const membersResponse = await axios.get(`/api/users/groups/${encodeURIComponent(entity.groupName || displayName)}/members`);

              // Füge alle Mitglieder-Emails hinzu
              for (const member of membersResponse.data) {
                if (member.mail && !emails.includes(member.mail)) {
                  emails.push(member.mail);
                  console.log(`[Mention] Gruppenmitglied: ${member.displayName} -> ${member.mail}`);
                }
              }
            } catch (groupError) {
              console.error(`[Mention] Fehler beim Laden der Gruppenmitglieder für "${displayName}":`, groupError);
            }
          } else if (entity.mail) {
            // Einzelner Benutzer
            emails.push(entity.mail);
            console.log(`[Mention] ✓ "${displayName}" -> ${entity.mail}`);
          }
        } else {
          console.warn(`[Mention] ✗ Kein Benutzer/Gruppe gefunden für: "${displayName}"`);
        }
      } catch (error) {
        console.error(`[Mention] Fehler beim Laden von "${displayName}":`, error);
      }
    }

    console.log(`[Mention] Gesamt: ${emails.length} E-Mail-Adressen extrahiert:`, emails);
    return emails;
  };

  const handleSaveKommentar = async () => {
    if (!neuerKommentar.trim()) {
      setError('Bitte geben Sie einen Kommentar ein');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // NEUE LOGIK: Verwende direkt die ausgewählten Mentions (User-Objekte bereits vorhanden!)
      const mentionEmails: string[] = [];

      // 1. Hole E-Mails aus selectedMentions (User die aus Dropdown ausgewählt wurden)
      for (const [displayName, user] of selectedMentions.entries()) {
        console.log(`[Mention] Verwende ausgewählten User: ${displayName} -> ${user.mail}`);

        // Prüfe ob es eine Gruppe ist
        if ((user as any).isGroup) {
          console.log(`[Mention] "${displayName}" ist eine Gruppe, lade Mitglieder...`);
          try {
            const membersResponse = await axios.get(`/api/users/groups/${encodeURIComponent((user as any).groupName || displayName)}/members`);
            for (const member of membersResponse.data) {
              if (member.mail && !mentionEmails.includes(member.mail)) {
                mentionEmails.push(member.mail);
                console.log(`[Mention] Gruppenmitglied: ${member.displayName} -> ${member.mail}`);
              }
            }
          } catch (groupError) {
            console.error(`[Mention] Fehler beim Laden der Gruppenmitglieder für "${displayName}":`, groupError);
          }
        } else if (user.mail && !mentionEmails.includes(user.mail)) {
          mentionEmails.push(user.mail);
        }
      }

      console.log(`[Mention] ${mentionEmails.length} E-Mail-Adressen aus ausgewählten Mentions:`, mentionEmails);

      if (selectedPositionId) {
        // Position-Kommentar
        const position = positionen.find((p) => p.Id === selectedPositionId);
        if (position) {
          await kommentareApi.createPositionKommentar(
            bestellungId,
            selectedPositionId,
            position.PosID || 0,
            neuerKommentar,
            user?.email || '', // Author Email
            mentionEmails.length > 0 ? mentionEmails : undefined,
            replyTo || undefined
          );
        }
      } else {
        // Bestellung-Kommentar
        await kommentareApi.createBestellungKommentar(
          bestellungId,
          neuerKommentar,
          user?.email || '', // Author Email
          mentionEmails.length > 0 ? mentionEmails : undefined,
          replyTo || undefined
        );
      }

      setNeuerKommentar('');
      setReplyTo(null);
      setSelectedPositionId(null);
      setSelectedMentions(new Map()); // Zurücksetzen der ausgewählten Mentions
      await loadKommentare();
      if (onKommentarChange) {
        onKommentarChange();
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Speichern des Kommentars');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteKommentar = async (kommentarId: string, typ: 'bestellung' | 'position') => {
    if (!window.confirm('Möchten Sie diesen Kommentar wirklich löschen?')) {
      return;
    }

    try {
      setError(null);
      // Entferne das Präfix (b_ oder p_) um die echte ID zu erhalten
      const realId = kommentarId.replace(/^[bp]_/, '');
      await kommentareApi.deleteKommentar(parseInt(realId, 10), typ);
      await loadKommentare();
      if (onKommentarChange) {
        onKommentarChange();
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Löschen des Kommentars');
    }
  };

  // Ist Kommentar innerhalb von 15 Minuten erstellt worden?
  const isWithin15Minutes = (created: Date | null): boolean => {
    if (!created) return false;
    return (Date.now() - created.getTime()) <= 15 * 60 * 1000;
  };

  // Darf der aktuelle User diesen Kommentar bearbeiten?
  const canEditComment = (kommentar: IKommentarAnzeige): boolean => {
    const isOwn = kommentar.author.toLowerCase() === (user?.email?.toLowerCase() || '');
    if (!isOwn) return false;
    if (currentUserPermissions?.isAdmin || currentUserPermissions?.isEinkauf) return true;
    return isWithin15Minutes(kommentar.created);
  };

  const handleStartEdit = (kommentar: IKommentarAnzeige) => {
    const newMap = new Map(editingStates);
    newMap.set(kommentar.id, kommentar.kommentartext);
    setEditingStates(newMap);
  };

  const handleCancelEdit = (kommentarId: string) => {
    const newMap = new Map(editingStates);
    newMap.delete(kommentarId);
    setEditingStates(newMap);
  };

  const handleSaveEdit = async (kommentar: IKommentarAnzeige, newText: string) => {
    const realId = parseInt(kommentar.id.replace(/^[bp]_/, ''), 10);
    setSavingEditId(kommentar.id);
    try {
      await kommentareApi.updateKommentar(realId, newText, kommentar.erwaehntPersonen);
      handleCancelEdit(kommentar.id);
      await loadKommentare();
      if (onKommentarChange) onKommentarChange();
    } catch (err: any) {
      setError(err.message || 'Fehler beim Bearbeiten des Kommentars');
    } finally {
      setSavingEditId(null);
    }
  };

  const handleToggleTemporaryPermission = async (
    userEmail: string,
    userName: string,
    positionId: number | undefined,
    currentlyGranted: boolean
  ) => {
    try {
      if (currentlyGranted) {
        // Widerrufe alle Berechtigungen für diesen Benutzer auf dieser Ressource
        await axios.post('/api/temporary-permissions/revoke-all', {
          bestellungId,
          positionId: positionId || undefined,
          userEmail,
        });
      } else {
        // Erteile Berechtigung
        await axios.post('/api/temporary-permissions', {
          userEmail,
          userDisplayName: userName,
          bestellungId,
          positionId: positionId || undefined,
          canEdit: true,
          reason: 'Erwähnt in Kommentar',
        });
      }

      // Aktualisiere temporäre Berechtigungen
      await loadTemporaryPermissions();
    } catch (err: any) {
      setError(err.message || 'Fehler beim Ändern der temporären Berechtigung');
    }
  };

  // Extrahiere Text aus SharePoint HTML (entfernt div-Tags)
  const extractTextFromHtml = (html: string): string => {
    if (!html) return '';
    // Entferne HTML-Tags aber behalte den Inhalt
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  // Konvertiere Email-Adressen zurück zu DisplayNames
  const convertEmailsToDisplayNames = async (emails: string[]): Promise<Map<string, string>> => {
    const emailToNameMap = new Map<string, string>();

    for (const email of emails) {
      try {
        // Suche Benutzer nach Email
        const response = await axios.get(`/api/users/search?q=${encodeURIComponent(email)}`);

        // Finde Benutzer mit passender Email
        const user = response.data.find(
          (u: IUserSuggestion) => u.mail && u.mail.toLowerCase() === email.toLowerCase()
        );

        if (user && user.displayName) {
          emailToNameMap.set(email, user.displayName);
        }
      } catch (error) {
        console.error(`[Mention] Fehler beim Laden von Benutzer "${email}":`, error);
      }
    }

    return emailToNameMap;
  };

  // Rendere erwähnte Personen als SharePoint-style Badges
  const renderMentionedPersons = (kommentar: IKommentarAnzeige) => {
    if (!kommentar.erwaehntPersonen || kommentar.erwaehntPersonen.length === 0) {
      return null;
    }

    return (
      <Stack
        horizontal
        tokens={{ childrenGap: 6 }}
        wrap
        styles={{ root: { marginBottom: 8 } }}
      >
        {kommentar.erwaehntPersonen.map((email) => {
          const displayName = emailToNameMap.get(email) || email;
          return (
            <Stack
              key={email}
              horizontal
              tokens={{ childrenGap: 4 }}
              verticalAlign="center"
              styles={{
                root: {
                  backgroundColor: '#DEECF9',
                  border: '1px solid #0078D4',
                  borderRadius: 4,
                  padding: '2px 8px',
                  display: 'inline-flex',
                },
              }}
            >
              <Icon
                iconName="Contact"
                styles={{ root: { color: '#0078D4', fontSize: 12 } }}
              />
              <Text
                variant="small"
                styles={{
                  root: {
                    color: '#0078D4',
                    fontWeight: 500,
                  },
                }}
              >
                {displayName}
              </Text>
            </Stack>
          );
        })}
      </Stack>
    );
  };

  // Rendere Slider für temporäre Berechtigungen (nur für Admin/Einkauf/GF/Freigeben)
  const renderTemporaryPermissionToggles = (kommentar: IKommentarAnzeige) => {
    // Nur anzeigen wenn:
    // 1. Erwähnte Personen vorhanden
    // 2. Aktueller Benutzer kann Berechtigungen erteilen
    if (!kommentar.erwaehntPersonen || kommentar.erwaehntPersonen.length === 0) {
      return null;
    }

    const canGrantPermissions = currentUserPermissions && (
      currentUserPermissions.isAdmin ||
      currentUserPermissions.isEinkauf ||
      currentUserPermissions.isGeschaeftsfuehrer ||
      currentUserPermissions.canApprove
    );

    if (!canGrantPermissions) {
      return null;
    }

    // Filtere nur View-User (die keine Edit-Rechte haben)
    const viewUsers = kommentar.erwaehntPersonen.filter((email) => {
      const userPerms = mentionedUsersPermissions.get(email);
      return userPerms && !userPerms.canEdit;
    });

    if (viewUsers.length === 0) {
      return null;
    }

    return (
      <Stack
        tokens={{ childrenGap: 8 }}
        styles={{
          root: {
            backgroundColor: '#fff4ce',
            border: '1px solid #ffb900',
            borderRadius: 4,
            padding: 8,
            marginBottom: 8,
          },
        }}
      >
        <Text
          variant="small"
          styles={{
            root: {
              color: '#605e5c',
              fontWeight: 600,
            },
          }}
        >
          <Icon iconName="Permissions" styles={{ root: { marginRight: 6 } }} />
          Temporäre Bearbeitungsrechte erteilen:
        </Text>
        {viewUsers.map((email) => {
          const displayName = emailToNameMap.get(email) || email;
          const key = `${email}_${bestellungId}_${kommentar.positionId || 'null'}`;
          const isGranted = temporaryPermissions.get(key) || false;

          return (
            <Stack
              key={email}
              horizontal
              verticalAlign="center"
              tokens={{ childrenGap: 8 }}
            >
              <Toggle
                checked={isGranted}
                onChange={(_, checked) =>
                  handleToggleTemporaryPermission(
                    email,
                    displayName,
                    kommentar.positionId,
                    !checked
                  )
                }
                onText={`${displayName} - Berechtigung erteilt`}
                offText={`${displayName} - Keine Berechtigung`}
                styles={{
                  root: { marginBottom: 0 },
                }}
              />
              {isGranted && (
                <Icon
                  iconName="CheckMark"
                  styles={{ root: { color: '#107c10', fontSize: 16 } }}
                />
              )}
            </Stack>
          );
        })}
      </Stack>
    );
  };

  // Rendere Kommentartext mit Mention-Highlighting
  const renderKommentarText = (text: string, mentions?: string[]) => {
    // Extrahiere Text aus HTML
    let plainText = extractTextFromHtml(text);

    // Entferne Author-Präfix am Anfang (z.B. "@IT-Support:" oder "@Schumacher, Sven:")
    // Regex: Entferne "@Benutzername:" am Anfang des Textes
    plainText = plainText.replace(/^@[\wÄÖÜäöüß\-]+(?:[\s,][\wÄÖÜäöüß\-]+)*:\s*/,  '');

    if (!mentions || mentions.length === 0) {
      return <span>{plainText}</span>;
    }

    // Regex für @-Mentions im Text (gleiche wie in extractMentions)
    const mentionRegex = /@([\wÄÖÜäöüß\-]+(?:[\s,][\wÄÖÜäöüß\-]+)*)(?=\s*[\.\!\?\n\r]|\s{2,}|$)/g;

    // Finde alle @-Mentions im Text
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;

    while ((match = mentionRegex.exec(plainText)) !== null) {
      const displayName = match[1].trim().replace(/,\s*$/, ''); // Entferne trailing Kommas und Leerzeichen
      const matchStart = match.index;
      const matchEnd = match.index + match[0].length;

      // Text vor dem Match
      if (matchStart > lastIndex) {
        parts.push(plainText.substring(lastIndex, matchStart));
      }

      // Prüfe ob dieser DisplayName in den erwähnten Personen ist
      const isValidMention = Array.from(emailToNameMap.values()).some(
        name => name.toLowerCase() === displayName.toLowerCase()
      );

      // Highlight nur wenn es eine valide Erwähnung ist
      if (isValidMention) {
        parts.push(
          <span
            key={matchStart}
            style={{
              backgroundColor: '#DEECF9',
              border: '1px solid #0078D4',
              borderRadius: 3,
              padding: '1px 4px',
              color: '#0078D4',
              fontWeight: 500,
            }}
          >
            @{displayName}
          </span>
        );
      } else {
        // Nicht-valide Erwähnung als normaler Text
        parts.push(match[0]);
      }

      lastIndex = matchEnd;
    }

    // Restlicher Text nach dem letzten Match
    if (lastIndex < plainText.length) {
      parts.push(plainText.substring(lastIndex));
    }

    return <>{parts}</>;
  };

  // Positions-Badge als farbliche Bubble
  const renderPositionBadge = (kommentar: IKommentarAnzeige) => {
    if (kommentar.typ !== 'position' || !kommentar.posID) return null;

    const colors = getPositionColor(kommentar.posID);

    return (
      <Stack
        horizontal
        tokens={{ childrenGap: 6 }}
        verticalAlign="center"
        styles={{
          root: {
            backgroundColor: colors.bg,
            border: `1px solid ${colors.border}`,
            borderRadius: 16,
            padding: '4px 12px',
            display: 'inline-flex',
            marginBottom: 8,
          },
        }}
      >
        <Icon iconName="Package" styles={{ root: { color: colors.text, fontSize: 12 } }} />
        <Text
          variant="small"
          styles={{
            root: {
              color: colors.text,
              fontWeight: 600,
            },
          }}
        >
          Pos {kommentar.posID}: {kommentar.artikelname}
        </Text>
      </Stack>
    );
  };

  const renderKommentar = (kommentar: IKommentarAnzeige, isReply: boolean = false) => {
    const isOwnComment = kommentar.author.toLowerCase() === (user?.email?.toLowerCase() || '');
    const isEditing = editingStates.has(kommentar.id);
    const editText = editingStates.get(kommentar.id) ?? '';
    const isSavingThis = savingEditId === kommentar.id;
    const canEdit = canEditComment(kommentar);

    // Verbleibende Zeit im 15-Minuten-Fenster
    const remainingMinutes = kommentar.created
      ? Math.max(0, Math.ceil((15 * 60 * 1000 - (Date.now() - kommentar.created.getTime())) / 60000))
      : 0;

    return (
      <Stack
        key={kommentar.id}
        tokens={{ childrenGap: 8 }}
        styles={{
          root: {
            padding: 12,
            backgroundColor: isReply ? '#f8f8f8' : '#faf9f8',
            borderRadius: 4,
            marginLeft: isReply ? 32 : 0,
            borderLeft: isReply ? '3px solid #0078d4' : 'none',
            border: isReply ? 'none' : '1px solid #edebe9',
          },
        }}
      >
        {/* Position-Badge als farbliche Bubble */}
        {renderPositionBadge(kommentar)}

        {/* Autor und Datum */}
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
            <Persona
              text={kommentar.authorName}
              size={PersonaSize.size32}
              hidePersonaDetails={false}
              styles={{ root: { minWidth: 0 } }}
            />
            {kommentar.created && (
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                {kommentar.created.toLocaleString('de-DE')}
              </Text>
            )}
          </Stack>
          <Stack horizontal tokens={{ childrenGap: 0 }} verticalAlign="center">
            {canEdit && !isEditing && (
              <TooltipHost content={
                (currentUserPermissions?.isAdmin || currentUserPermissions?.isEinkauf)
                  ? 'Kommentar bearbeiten'
                  : `Kommentar bearbeiten (noch ${remainingMinutes} Min.)`
              }>
                <IconButton
                  iconProps={{ iconName: 'Edit' }}
                  title="Kommentar bearbeiten"
                  onClick={() => handleStartEdit(kommentar)}
                  styles={{ root: { color: '#0078d4' } }}
                />
              </TooltipHost>
            )}
            {isOwnComment && !isEditing && (
              <IconButton
                iconProps={{ iconName: 'Delete' }}
                title="Kommentar löschen"
                onClick={() => handleDeleteKommentar(kommentar.id, kommentar.typ)}
              />
            )}
          </Stack>
        </Stack>

        {/* Erwähnte Personen als Badges */}
        {renderMentionedPersons(kommentar)}

        {/* Temporäre Berechtigungen (nur für Admin/Einkauf/GF/Freigeben) */}
        {!isEditing && renderTemporaryPermissionToggles(kommentar)}

        {/* Kommentartext oder Edit-Modus */}
        {isEditing ? (
          <Stack tokens={{ childrenGap: 8 }}>
            <TextField
              value={editText}
              onChange={(_, v) => {
                const newMap = new Map(editingStates);
                newMap.set(kommentar.id, v ?? '');
                setEditingStates(newMap);
              }}
              multiline
              rows={3}
              disabled={isSavingThis}
              autoFocus
            />
            <Stack horizontal tokens={{ childrenGap: 8 }}>
              <PrimaryButton
                text="Speichern"
                iconProps={{ iconName: 'Save' }}
                onClick={() => handleSaveEdit(kommentar, editText)}
                disabled={isSavingThis || !editText.trim()}
              />
              <DefaultButton
                text="Abbrechen"
                onClick={() => handleCancelEdit(kommentar.id)}
                disabled={isSavingThis}
              />
            </Stack>
          </Stack>
        ) : (
          <Text
            styles={{
              root: {
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              },
            }}
          >
            {renderKommentarText(kommentar.kommentartext, kommentar.erwaehntPersonen)}
          </Text>
        )}

        {/* Antworten-Button nur bei Haupt-Kommentaren (nicht im Edit-Modus) */}
        {!isReply && !isEditing && (
          <Stack horizontal>
            <DefaultButton
              text="Antworten"
              iconProps={{ iconName: 'Reply' }}
              onClick={() => {
                setReplyTo(kommentar.id);
                if (kommentar.typ === 'position' && kommentar.positionId) {
                  setSelectedPositionId(kommentar.positionId);
                } else {
                  setSelectedPositionId(null);
                }
              }}
              styles={{ root: { height: 28 } }}
            />
          </Stack>
        )}
      </Stack>
    );
  };

  const renderThread = (thread: IKommentarThread) => {
    return (
      <Stack key={thread.hauptKommentar.id} tokens={{ childrenGap: 8 }}>
        {/* Haupt-Kommentar */}
        {renderKommentar(thread.hauptKommentar, false)}

        {/* Antworten (aufsteigend sortiert - älteste zuerst) */}
        {thread.antworten.map((antwort) => renderKommentar(antwort, true))}
      </Stack>
    );
  };

  // Gesamtzahl der Kommentare
  const totalKommentare = threads.reduce(
    (sum, thread) => sum + 1 + thread.antworten.length,
    0
  );

  return (
    <Stack
      tokens={{ childrenGap: 16 }}
      styles={{
        root: {
          padding: '20px',
          backgroundColor: 'white',
          boxShadow: '0 1.6px 3.6px 0 rgba(0,0,0,.132), 0 0.3px 0.9px 0 rgba(0,0,0,.108)',
          borderRadius: '2px',
        },
      }}
    >
      <Text variant="large" styles={{ root: { fontWeight: 600 } }}>
        Kommentare
      </Text>

      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
          {error}
        </MessageBar>
      )}

      {/* Neuer Kommentar */}
      <Stack tokens={{ childrenGap: 12 }}>
        {replyTo && (
          <MessageBar
            messageBarType={MessageBarType.info}
            onDismiss={() => {
              setReplyTo(null);
              setSelectedPositionId(null);
              setSelectedMentions(new Map()); // Zurücksetzen der ausgewählten Mentions
            }}
            dismissButtonAriaLabel="Antwort abbrechen"
          >
            Antwort auf Kommentar...
          </MessageBar>
        )}

        {/* Position auswählen */}
        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="end" wrap>
          <Stack.Item grow>
            <Text variant="small" styles={{ root: { marginBottom: 4, display: 'block' } }}>
              Kommentar für:
            </Text>
            <Stack horizontal tokens={{ childrenGap: 8 }} wrap>
              <DefaultButton
                text="Bestellung"
                primary={selectedPositionId === null}
                onClick={() => setSelectedPositionId(null)}
              />
              {positionen.map((pos) => {
                const colors = getPositionColor(pos.PosID || 1);
                const isSelected = selectedPositionId === pos.Id;
                return (
                  <DefaultButton
                    key={pos.Id}
                    text={`Pos ${pos.PosID}`}
                    title={pos.Artikelname || ''}
                    onClick={() => setSelectedPositionId(pos.Id || null)}
                    styles={{
                      root: {
                        backgroundColor: isSelected ? colors.bg : undefined,
                        borderColor: isSelected ? colors.border : undefined,
                        color: isSelected ? colors.text : undefined,
                      },
                      rootHovered: {
                        backgroundColor: colors.bg,
                        borderColor: colors.border,
                      },
                    }}
                  />
                );
              })}
            </Stack>
          </Stack.Item>
        </Stack>

        <div ref={calloutTargetRef}>
          <TextField
            label="Neuer Kommentar"
            placeholder="Schreiben Sie hier Ihren Kommentar... (@ für Erwähnung)"
            value={neuerKommentar}
            onChange={(_, value) => handleTextChange(value || '')}
            multiline
            rows={4}
            disabled={saving}
          />
        </div>

        {/* @-Mention Vorschläge */}
        {showUserSuggestions && userSuggestions.length > 0 && (
          <Callout
            target={calloutTargetRef.current}
            onDismiss={() => setShowUserSuggestions(false)}
            directionalHint={DirectionalHint.bottomLeftEdge}
            isBeakVisible={false}
            styles={{ root: { padding: 0 } }}
          >
            <Stack styles={{ root: { maxHeight: 200, overflowY: 'auto' } }}>
              {userSuggestions.map((user) => (
                <Stack
                  key={user.id}
                  horizontal
                  tokens={{ childrenGap: 8, padding: 8 }}
                  verticalAlign="center"
                  styles={{
                    root: {
                      cursor: 'pointer',
                      ':hover': { backgroundColor: '#f3f2f1' },
                    },
                  }}
                  onClick={() => handleSelectUser(user)}
                >
                  <Persona
                    text={user.displayName}
                    secondaryText={user.mail}
                    size={PersonaSize.size24}
                  />
                </Stack>
              ))}
            </Stack>
          </Callout>
        )}

        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <PrimaryButton
            text={replyTo ? 'Antworten' : 'Kommentar hinzufügen'}
            iconProps={{ iconName: replyTo ? 'Reply' : 'Comment' }}
            onClick={handleSaveKommentar}
            disabled={saving || !neuerKommentar.trim()}
          />
          {(replyTo || selectedPositionId) && (
            <DefaultButton
              text="Abbrechen"
              onClick={() => {
                setReplyTo(null);
                setSelectedPositionId(null);
                setNeuerKommentar('');
                setSelectedMentions(new Map()); // Zurücksetzen der ausgewählten Mentions
              }}
              disabled={saving}
            />
          )}
        </Stack>

        <MessageBar messageBarType={MessageBarType.info}>
          Verwenden Sie @ um Personen zu erwähnen. Diese werden für spätere Benachrichtigungen gespeichert.
        </MessageBar>
      </Stack>

      <Separator />

      {/* Kommentare anzeigen */}
      {loading ? (
        <Stack horizontalAlign="center" tokens={{ padding: 40 }}>
          <Spinner label="Lade Kommentare..." />
        </Stack>
      ) : threads.length === 0 ? (
        <MessageBar messageBarType={MessageBarType.info}>
          Noch keine Kommentare vorhanden.
        </MessageBar>
      ) : (
        <Stack tokens={{ childrenGap: 16 }}>
          <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
            {totalKommentare} {totalKommentare === 1 ? 'Kommentar' : 'Kommentare'}
          </Text>
          {threads.map(renderThread)}
        </Stack>
      )}
    </Stack>
  );
};
